import { get } from 'lodash';
import i18next from 'i18next';
import Radio from '@mui/material/Radio';
import React, { useState, useEffect } from 'react';

import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getTheme } from '@commons/utils/theme';
import { ToggleSwitch, Tooltip } from '@commons/utils/styledLibraryComponents';
import Text, { ENUM_FONTS, ENUM_COLORS } from '@commons/Text';

import { supplier as supplierService } from '@services/supplier';

/**
 * @typedef {Object} SspmAvailabilities
 * @property {boolean} available
 * @property {boolean} hasStock
 * @property {boolean} hasDlc
 */
import { EDI_FLOWS_TYPES } from '../constants';

import {
  Container,
  ToggleContainer,
  ContentContainer,
  ChoicesContainer,
  RadioButtonContainer,
  RadioChoice,
} from './styledComponents';
import { TooltipIcon } from '@commons/Metrics/styledComponents';

export const ManageEdiFlowsModalContent = ({
  ediPartner,
  selectedEdiFlows,
  handleEdiFlowsUpdate,
}) => {
  const theme = getTheme();

  const [ediFeatures, setEdiFeatures] = useState({});

  useEffect(() => {
    if (!ediPartner) {
      return;
    }

    (async function loadData() {
      const availableEdiFeatures = await supplierService.getEdiFeatures(ediPartner);

      setEdiFeatures(availableEdiFeatures);
    })();
  }, [ediPartner]);

  const getValueOfKey = (key) => get(selectedEdiFlows, key, null);

  const getRadioSxStyle = (theme) => ({
    color: theme.colors.blacks.ipBlack1,
    '& .MuiSvgIcon-root': {
      fontSize: 18,
      'border-width': '1px',
    },
    '&.Mui-checked': {
      color: theme.colors.blacks.ipBlack1,
    },
  });

  const renderToggleSwitch = (key) => (
    <ToggleSwitch
      checked={getValueOfKey(key) !== null}
      handleClick={() => handleEdiFlowsUpdate('toggle', key, null)}
      id={`update-${key}`}
    />
  );

  const renderTruthyRadio = (key) => (
    <Radio
      checked={getValueOfKey(key)}
      disableColor={theme.colors.greys.lighty}
      disabled={getValueOfKey(key) === null}
      sx={getRadioSxStyle(theme)}
      value={getValueOfKey(key)}
      onChange={() => handleEdiFlowsUpdate('radio', key, true)}
    />
  );

  const renderFalsyRadio = (key) => (
    <Radio
      checked={getValueOfKey(key) !== null && getValueOfKey(key) === false}
      disableColor={theme.colors.greys.lighty}
      disabled={getValueOfKey(key) === null}
      sx={getRadioSxStyle(theme)}
      value={getValueOfKey(key) === false}
      onChange={() => handleEdiFlowsUpdate('radio', key, false)}
    />
  );

  const renderRadioLabel = (key, label) => (
    <Text color={getValueOfKey(key) === null ? ENUM_COLORS.LIGHTY : ENUM_COLORS.DARKEST}>
      {label}
    </Text>
  );

  return (
    <Container>
      <ContentContainer>
        <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}>
          {i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_FLOWS_INFO')}
        </Text>

        <ChoicesContainer>
          <ToggleContainer>
            {renderToggleSwitch(EDI_FLOWS_TYPES.ORDER)}
            <Text>{i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_ORDER_LABEL')}</Text>
            <Tooltip
              renderTooltipTrigger={() => (
                <TooltipIcon src={'/images/inpulse/info-black-small.svg'} />
              )}
              text={i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_ORDER_TOOLTIP')}
            />
          </ToggleContainer>

          <RadioButtonContainer>
            <RadioChoice>
              {renderTruthyRadio(EDI_FLOWS_TYPES.ORDER)}
              {renderRadioLabel(EDI_FLOWS_TYPES.ORDER, i18next.t('GENERAL.ACTIVATE'))}
            </RadioChoice>
            <RadioChoice>
              {renderFalsyRadio(EDI_FLOWS_TYPES.ORDER)}
              {renderRadioLabel(EDI_FLOWS_TYPES.ORDER, i18next.t('GENERAL.DISABLE'))}
            </RadioChoice>
          </RadioButtonContainer>
        </ChoicesContainer>

        {ediFeatures.bli && (
          <ChoicesContainer>
            <ToggleContainer>
              {renderToggleSwitch(EDI_FLOWS_TYPES.BLI)}
              <Text>{i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_BLI_LABEL')}</Text>
              <Tooltip
                renderTooltipTrigger={() => (
                  <TooltipIcon src={'/images/inpulse/info-black-small.svg'} />
                )}
                text={i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_BLI_TOOLTIP')}
              />
            </ToggleContainer>

            <RadioButtonContainer>
              <RadioChoice>
                {renderTruthyRadio(EDI_FLOWS_TYPES.BLI)}
                {renderRadioLabel(EDI_FLOWS_TYPES.BLI, i18next.t('GENERAL.ACTIVATE'))}
              </RadioChoice>
              <RadioChoice>
                {renderFalsyRadio(EDI_FLOWS_TYPES.BLI)}
                {renderRadioLabel(EDI_FLOWS_TYPES.BLI, i18next.t('GENERAL.DISABLE'))}
              </RadioChoice>
            </RadioButtonContainer>
          </ChoicesContainer>
        )}

        {ediFeatures.planning && (
          <ChoicesContainer>
            <ToggleContainer>
              {renderToggleSwitch(EDI_FLOWS_TYPES.PLANNING)}
              <Text>{i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_PLANNING_LABEL')}</Text>
              <Tooltip
                renderTooltipTrigger={() => (
                  <TooltipIcon src={'/images/inpulse/info-black-small.svg'} />
                )}
                text={i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_PLANNING_TOOLTIP')}
              />
            </ToggleContainer>

            <RadioButtonContainer>
              <RadioChoice>
                {renderTruthyRadio(EDI_FLOWS_TYPES.PLANNING)}
                {renderRadioLabel(EDI_FLOWS_TYPES.PLANNING, i18next.t('GENERAL.ACTIVATE'))}
              </RadioChoice>
              <RadioChoice>
                {renderFalsyRadio(EDI_FLOWS_TYPES.PLANNING)}
                {renderRadioLabel(EDI_FLOWS_TYPES.PLANNING, i18next.t('GENERAL.DISABLE'))}
              </RadioChoice>
            </RadioButtonContainer>
          </ChoicesContainer>
        )}
      </ContentContainer>
    </Container>
  );
};

/**
 * Display the modal to update EDI flows,
 *
 * @param {Object} selectedEdiFlows
 * @param {Function} setSelectedEdiFlows
 * @param {Function} handleEdiFlowsUpdate
 * @param {Function} handleEdiFlowsSave
 */
export const getManageEdiFlowsModalConfig = ({
  ediPartner,
  selectedEdiFlows,
  setSelectedEdiFlows,
  handleEdiFlowsUpdate,
  handleEdiFlowsSave,
  handleEdiFlowsCloseCleanUp,
}) => ({
  actions: [
    GENERIC_MODAL_CANCEL_BUTTON(),
    {
      ...GENERIC_MODAL_SAVE_BUTTON(),
      handleClick: handleEdiFlowsSave,
      preventClosing: true,
      isDisabled: Object.keys(selectedEdiFlows).length === 0,
    },
  ],
  title: i18next.t('ADMIN.SUPPLIERS.MANAGE_EDI_FLOWS'),
  icon: '/images/inpulse/pen-black-small.svg',
  type: 'action',
  handleCloseCleanUp: handleEdiFlowsCloseCleanUp,
  component: ManageEdiFlowsModalContent,
  data: {
    ediPartner,
    selectedEdiFlows,
    setSelectedEdiFlows,
    handleEdiFlowsUpdate,
  },
  height: 'auto',
});
